import { AxiosError } from 'axios';
import { IBuilding, IEhs, IProject, IPtw, IUser, IWorkstation } from '@relab/alstom-ptw-shared';

export interface ServerRequestStatus {
  pending: boolean;
  success: boolean;
  error: string;
  payload?: RequestPayload;
}
export interface Server {
  status: string;
  requests: {
    [key: string]: ServerRequestStatus;
  };
}

export interface AppState {
  server: Server;
  user?: IUser;
  users?: IUser[];
  project?: IProject;
  projects?: IProject[];
  workstations?: IWorkstation[];
  buildings?: IBuilding[];
  ptws?: IPtw[];
  ptw?: IPtw;
  ehs?: IEhs;
  ehss: IEhs[];
}

export interface RootState {
  app: AppState;
  router: any;
}

export interface RequestPayload {
  request: {
    url: string;
    method?: string;
    data?: any;
  };
  config?: any;
  data?: any;
  headers?: { [key: string]: string };
  status?: number;
  statusText?: string;
  user?: Partial<IUser>;
  project?: Partial<IProject>;
  workstation?: any;
  ptw?: Partial<IPtw>;
  ehs?: Partial<IEhs>;
}

export enum ActionType {
  SERVER_STATE = 'SERVER_STATE',
  SERVER_STATE_SUCCESS = 'SERVER_STATE_SUCCESS',
  SERVER_STATE_FAIL = 'SERVER_STATE_FAIL',

  RESET_SERVER_REQUEST = 'RESET_SERVER_REQUEST',

  SIGN_IN = 'SIGN_IN',
  SIGN_IN_SUCCESS = 'SIGN_IN_SUCCESS',
  SIGN_IN_FAIL = 'SIGN_IN_FAIL',

  SIGN_OUT = 'SIGN_OUT',

  SIGN_UP = 'SIGN_UP',
  SIGN_UP_SUCCESS = 'SIGN_UP_SUCCESS',
  SIGN_UP_FAIL = 'SIGN_UP_FAIL',

  RESET_PASSWORD_REQUEST = 'RESET_PASSWORD_REQUEST',
  RESET_PASSWORD_REQUEST_SUCCESS = 'RESET_PASSWORD_REQUEST_SUCCESS',
  RESET_PASSWORD_REQUEST_FAIL = 'RESET_PASSWORD_REQUEST_FAIL',

  RESET_PASSWORD = 'RESET_PASSWORD',
  RESET_PASSWORD_SUCCESS = 'RESET_PASSWORD_SUCCESS',
  RESET_PASSWORD_FAIL = 'RESET_PASSWORD_FAIL',

  CHECK_LOGIN = 'CHECK_LOGIN',
  CHECK_LOGIN_SUCCESS = 'CHECK_LOGIN_SUCCESS',
  CHECK_LOGIN_FAIL = 'CHECK_LOGIN_FAIL',

  USERS = 'USERS',
  USERS_SUCCESS = 'USERS_SUCCESS',
  USERS_FAIL = 'USERS_FAIL',

  USER_EDIT = 'USER_EDIT',
  USER_EDIT_SUCCESS = 'USER_EDIT_SUCCESS',
  USER_EDIT_FAIL = 'USER_EDIT_FAIL',

  USER_DELETE = 'USER_DELETE',
  USER_DELETE_SUCCESS = 'USER_DELETE_SUCCESS',
  USER_DELETE_FAIL = 'USER_DELETE_FAIL',

  PROJECT_CREATE = 'PROJECT_CREATE',
  PROJECT_CREATE_SUCCESS = 'PROJECT_CREATE_SUCCESS',
  PROJECT_CREATE_FAIL = 'PROJECT_CREATE_FAIL',

  PROJECT = 'PROJECT',
  PROJECT_SUCCESS = 'PROJECT_SUCCESS',
  PROJECT_FAIL = 'PROJECT_FAIL',

  PROJECTS = 'PROJECTS',
  PROJECTS_SUCCESS = 'PROJECTS_SUCCESS',
  PROJECTS_FAIL = 'PROJECTS_FAIL',

  PROJECT_UPSERT = 'PROJECT_UPSERT',

  PROJECT_DELETE = 'PROJECT_DELETE',
  PROJECT_DELETE_SUCCESS = 'PROJECT_DELETE_SUCCESS',
  PROJECT_DELETE_FAIL = 'PROJECT_DELETE_FAIL',

  WORKSTATION_CREATE = 'WORKSTATION_CREATE',
  WORKSTATION_CREATE_SUCCESS = 'WORKSTATION_CREATE_SUCCESS',
  WORKSTATION_CREATE_FAIL = 'WORKSTATION_CREATE_FAIL',

  WORKSTATIONS = 'WORKSTATIONS',
  WORKSTATIONS_SUCCESS = 'WORKSTATIONS_SUCCESS',
  WORKSTATIONS_FAIL = 'WORKSTATIONS_FAIL',

  WORKSTATION_UPSERT = 'WORKSTATION_UPSERT',

  WORKSTATION_DELETE = 'WORKSTATION_DELETE',
  WORKSTATION_DELETE_SUCCESS = 'WORKSTATION_DELETE_SUCCESS',
  WORKSTATION_DELETE_FAIL = 'WORKSTATION_DELETE_FAIL',

  BUILDINGS = 'BUILDINGS',
  BUILDINGS_SUCCESS = 'BUILDINGS_SUCCESS',
  BUILDINGS_FAIL = 'BUILDINGS_FAIL',

  BUILDING_UPSERT = 'BUILDING_UPSERT',

  BUILDING_DELETE = 'BUILDING_DELETE',
  BUILDING_DELETE_SUCCESS = 'BUILDING_DELETE_SUCCESS',
  BUILDING_DELETE_FAIL = 'BUILDING_DELETE_FAIL',

  ASSIGNEE_ADD = 'ASSIGNEE_ADD',
  ASSIGNEE_ADD_SUCCESS = 'ASSIGNEE_ADD_SUCCESS',
  ASSIGNEE_ADD_FAIL = 'ASSIGNEE_ADD_FAIL',

  ASSIGNEE_REMOVE = 'ASSIGNEE_REMOVE',
  ASSIGNEE_REMOVE_SUCCESS = 'ASSIGNEE_REMOVE_SUCCESS',
  ASSIGNEE_REMOVE_FAIL = 'ASSIGNEE_REMOVE_FAIL',

  TRAIN_CREATE = 'TRAIN_CREATE',
  TRAIN_CREATE_SUCCESS = 'TRAIN_CREATE_SUCCESS',
  TRAIN_CREATE_FAIL = 'TRAIN_CREATE_FAIL',

  TRAIN_DELETE = 'TRAIN_DELETE',
  TRAIN_DELETE_SUCCESS = 'TRAIN_DELETE_SUCCESS',
  TRAIN_DELETE_FAIL = 'TRAIN_DELETE_FAIL',

  PTW_CREATE = 'PTW_CREATE',
  PTW_CREATE_SUCCESS = 'PTW_CREATE_SUCCESS',
  PTW_CREATE_FAIL = 'PTW_CREATE_FAIL',

  PTW = 'PTW',
  PTW_SUCCESS = 'PTW_SUCCESS',
  PTW_FAIL = 'PTW_FAIL',

  PTW_EDIT = 'PTW_EDIT',
  PTW_EDIT_SUCCESS = 'PTW_EDIT_SUCCESS',
  PTW_EDIT_FAIL = 'PTW_EDIT_FAIL',

  PTW_UPDATE = 'PTW_UPDATE',

  PTW_DELETE = 'PTW_DELETE',
  PTW_DELETE_SUCCESS = 'PTW_DELETE_SUCCESS',
  PTW_DELETE_FAIL = 'PTW_DELETE_FAIL',

  EHS_CREATE = 'EHS_CREATE',
  EHS_CREATE_SUCCESS = 'EHS_CREATE_SUCCESS',
  EHS_CREATE_FAIL = 'EHS_CREATE_FAIL',

  EHS = 'EHS',
  EHS_SUCCESS = 'EHS_SUCCESS',
  EHS_FAIL = 'EHS_FAIL',

  EHSS = 'EHSS',
  EHSS_SUCCESS = 'EHSS_SUCCESS',
  EHSS_FAIL = 'EHSS_FAIL',

  EHS_UPDATE = 'EHS_UPDATE',
  EHS_UPDATE_SUCCESS = 'EHS_UPDATE_SUCCESS',
  EHS_UPDATE_FAIL = 'EHS_UPDATE_FAIL',

  EHS_DELETE = 'EHS_DELETE',
  EHS_DELETE_SUCCESS = 'EHS_DELETE_SUCCESS',
  EHS_DELETE_FAIL = 'EHS_DELETE_FAIL',
}

export interface BasicAction {
  type: string;
  payload: Partial<RequestPayload>;
}

export interface RequestAction extends BasicAction {
  payload: RequestPayload;
  meta?: {
    previousAction: any;
  };
  error?: AxiosError;
}

interface ServerStateAction extends RequestAction {
  type: ActionType.SERVER_STATE | ActionType.SERVER_STATE_SUCCESS | ActionType.SERVER_STATE_FAIL;
}

interface ResetServerRequestAction {
  type: ActionType.RESET_SERVER_REQUEST;
  payload: ActionType;
}

interface SignInAction extends RequestAction {
  type: ActionType.SIGN_IN | ActionType.SIGN_IN_SUCCESS | ActionType.SIGN_IN_FAIL;
}

interface SignOutAction {
  type: ActionType.SIGN_OUT;
}

interface SignUpAction extends RequestAction {
  type: ActionType.SIGN_UP | ActionType.SIGN_UP_SUCCESS | ActionType.SIGN_UP_FAIL;
}

interface ResetPasswordRequestAction extends RequestAction {
  type:
    | ActionType.RESET_PASSWORD_REQUEST
    | ActionType.RESET_PASSWORD_REQUEST_SUCCESS
    | ActionType.RESET_PASSWORD_REQUEST_FAIL;
}

interface ResetPasswordAction extends RequestAction {
  type: ActionType.RESET_PASSWORD | ActionType.RESET_PASSWORD_SUCCESS | ActionType.RESET_PASSWORD_FAIL;
}

interface CheckLoginAction extends RequestAction {
  type: ActionType.CHECK_LOGIN | ActionType.CHECK_LOGIN_SUCCESS | ActionType.CHECK_LOGIN_FAIL;
}

interface UsersAction extends RequestAction {
  type: ActionType.USERS | ActionType.USERS_SUCCESS | ActionType.USERS_FAIL;
}

interface UserEditAction extends RequestAction {
  type: ActionType.USER_EDIT | ActionType.USER_EDIT_SUCCESS | ActionType.USER_EDIT_FAIL;
}

interface UserDeleteAction extends RequestAction {
  type: ActionType.USER_DELETE | ActionType.USER_DELETE_SUCCESS | ActionType.USER_DELETE_FAIL;
}

interface ProjectCreateAction extends RequestAction {
  type: ActionType.PROJECT_CREATE | ActionType.PROJECT_CREATE_SUCCESS | ActionType.PROJECT_CREATE_FAIL;
}

interface ProjectAction extends RequestAction {
  type: ActionType.PROJECT | ActionType.PROJECT_SUCCESS | ActionType.PROJECT_FAIL;
}

interface ProjectsAction extends RequestAction {
  type: ActionType.PROJECTS | ActionType.PROJECTS_SUCCESS | ActionType.PROJECTS_FAIL;
}

interface ProjectUpsertAction {
  type: ActionType.PROJECT_UPSERT;
  payload: {
    project: IProject;
  };
}

interface ProjectDeleteAction extends RequestAction {
  type: ActionType.PROJECT_DELETE | ActionType.PROJECT_DELETE_SUCCESS | ActionType.PROJECT_DELETE_FAIL;
}

interface WorkstationCreateAction extends RequestAction {
  type: ActionType.WORKSTATION_CREATE | ActionType.WORKSTATION_CREATE_SUCCESS | ActionType.WORKSTATION_CREATE_FAIL;
}

interface WorkstationsAction extends RequestAction {
  type: ActionType.WORKSTATIONS | ActionType.WORKSTATIONS_SUCCESS | ActionType.WORKSTATIONS_FAIL;
}

interface WorkstationUpsertAction {
  type: ActionType.WORKSTATION_UPSERT;
  payload: {
    workstation: IWorkstation;
  };
}

interface WorkstationDeleteAction extends RequestAction {
  type: ActionType.WORKSTATION_DELETE | ActionType.WORKSTATION_DELETE_SUCCESS | ActionType.WORKSTATION_DELETE_FAIL;
}

interface BuildingsAction extends RequestAction {
  type: ActionType.BUILDINGS | ActionType.BUILDINGS_SUCCESS | ActionType.BUILDINGS_FAIL;
}

interface BuildingUpsertAction {
  type: ActionType.BUILDING_UPSERT;
  payload: { building: IBuilding };
}

interface BuildingDeleteAction extends RequestAction {
  type: ActionType.BUILDING_DELETE | ActionType.BUILDING_DELETE_SUCCESS | ActionType.BUILDING_DELETE_FAIL;
  payload: RequestPayload & { buildingId: string };
}

interface AssigneeAddAction extends RequestAction {
  type: ActionType.ASSIGNEE_ADD | ActionType.ASSIGNEE_ADD_SUCCESS | ActionType.ASSIGNEE_ADD_FAIL;
}

interface AssigneeRemoveAction extends RequestAction {
  type: ActionType.ASSIGNEE_REMOVE | ActionType.ASSIGNEE_REMOVE_SUCCESS | ActionType.ASSIGNEE_REMOVE_FAIL;
  payload: RequestPayload & { projectId: string; assigneeId: string };
}

interface TrainCreateAction extends RequestAction {
  type: ActionType.TRAIN_CREATE | ActionType.TRAIN_CREATE_SUCCESS | ActionType.TRAIN_CREATE_FAIL;
}

interface TrainDeleteAction extends RequestAction {
  type: ActionType.TRAIN_DELETE | ActionType.TRAIN_DELETE_SUCCESS | ActionType.TRAIN_DELETE_FAIL;
  payload: RequestPayload & { projectId: string; trainId: string };
}

interface PtwCreateAction extends RequestAction {
  type: ActionType.PTW_CREATE | ActionType.PTW_CREATE_SUCCESS | ActionType.PTW_CREATE_FAIL;
}

interface PtwAction extends RequestAction {
  type: ActionType.PTW | ActionType.PTW_SUCCESS | ActionType.PTW_FAIL;
}

interface PtwEditAction extends RequestAction {
  type: ActionType.PTW_EDIT | ActionType.PTW_EDIT_SUCCESS | ActionType.PTW_EDIT_FAIL;
}

interface PtwUpdateAction extends BasicAction {
  type: ActionType.PTW_UPDATE;
}

interface PtwDeleteAction extends RequestAction {
  type: ActionType.PTW_DELETE | ActionType.PTW_DELETE_SUCCESS | ActionType.PTW_DELETE_FAIL;
}

interface EhsCreateAction extends RequestAction {
  type: ActionType.EHS_CREATE | ActionType.EHS_CREATE_SUCCESS | ActionType.EHS_CREATE_FAIL;
}

interface EhsAction extends RequestAction {
  type: ActionType.EHS | ActionType.EHS_SUCCESS | ActionType.EHS_FAIL;
}

interface EhssAction extends RequestAction {
  type: ActionType.EHSS | ActionType.EHSS_SUCCESS | ActionType.EHSS_FAIL;
}

interface EhsUpdateAction extends RequestAction {
  type: ActionType.EHS_UPDATE | ActionType.EHS_UPDATE_SUCCESS | ActionType.EHS_UPDATE_FAIL;
}

interface EhsDeleteAction extends RequestAction {
  type: ActionType.EHS_DELETE | ActionType.EHS_DELETE_SUCCESS | ActionType.EHS_DELETE_FAIL;
}

export type AppActionTypes =
  | ServerStateAction
  | ResetServerRequestAction
  | SignInAction
  | SignOutAction
  | SignUpAction
  | ResetPasswordRequestAction
  | ResetPasswordAction
  | CheckLoginAction
  | UsersAction
  | UserEditAction
  | UserDeleteAction
  | ProjectCreateAction
  | ProjectAction
  | ProjectsAction
  | ProjectUpsertAction
  | ProjectDeleteAction
  | WorkstationCreateAction
  | WorkstationsAction
  | WorkstationUpsertAction
  | WorkstationDeleteAction
  | BuildingsAction
  | BuildingUpsertAction
  | BuildingDeleteAction
  | AssigneeAddAction
  | AssigneeRemoveAction
  | TrainCreateAction
  | TrainDeleteAction
  | PtwCreateAction
  | PtwAction
  | PtwEditAction
  | PtwUpdateAction
  | PtwDeleteAction
  | EhsCreateAction
  | EhsAction
  | EhssAction
  | EhsUpdateAction
  | EhsDeleteAction;
